<template>
  <Teleport to="body">
    <div :id="modalId" ref="modalEl" class="modal fade" tabindex="-1">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title d-flex align-items-center gap-2">
              <i
                class="mdi mdi-alert-circle-outline cart-error__icon"
                aria-hidden="true"
              ></i>
              {{ resolvedTitle }}
            </h5>
            <button
              type="button"
              class="btn-close"
              :aria-label="closeLabel"
              @click="close"
            ></button>
          </div>

          <div class="modal-body">
            <p v-if="headline" class="mb-2 fw-medium text-body">
              {{ headline }}
            </p>
            <p
              v-for="(msg, i) in bodyMessages"
              :key="i"
              class="mb-2 last-mb-0 text-secondary"
            >
              {{ msg }}
            </p>
          </div>

          <div class="modal-footer">
            <template v-if="isOnCartPage">
              <button type="button" class="btn btn-dark px-4" @click="close">
                {{ acknowledgeLabel }}
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="close"
              >
                {{ secondaryLabel }}
              </button>
              <button type="button" class="btn btn-dark" @click="goToCart">
                {{ primaryLabel }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

interface ErrorItem {
  detail?: string;
  title?: string;
}

const props = defineProps<{
  modelValue: boolean;
  errors?: Array<ErrorItem | string>;
  title?: string;
  primaryLabel?: string;
  secondaryLabel?: string;
  acknowledgeLabel?: string;
  closeLabel?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

const router = useRouter();
const route = useRoute();
const modalEl = ref<HTMLElement | null>(null);
const modalId = `cart-error-modal-${Math.random().toString(36).slice(2, 9)}`;
let bsModal: { show: () => void; hide: () => void } | null = null;

const isOnCartPage = computed(() => route.name === "cart-detail");

const resolvedTitle = computed(() => {
  if (props.title) return props.title;
  return isOnCartPage.value
    ? "No es posible actualizar la cantidad"
    : "No es posible agregar al carrito";
});
const primaryLabel = computed(() => props.primaryLabel ?? "Ver mi carrito");
const secondaryLabel = computed(() => props.secondaryLabel ?? "Seguir comprando");
const acknowledgeLabel = computed(() => props.acknowledgeLabel ?? "Entendido");
const closeLabel = computed(() => props.closeLabel ?? "Cerrar");

const messages = computed<string[]>(() =>
  (props.errors ?? [])
    .flatMap((e) => {
      const raw = typeof e === "string" ? e : e?.detail ?? e?.title ?? "";
      return raw.split(/\n+/);
    })
    .map((m) => m.trim())
    .filter(Boolean),
);

const headline = computed(() => messages.value[0] ?? "");
const bodyMessages = computed(() => messages.value.slice(1));

function close() {
  emit("update:modelValue", false);
}

function goToCart() {
  close();
  router.push({ name: "cart-detail" });
}

onMounted(async () => {
  if (!modalEl.value) return;
  const { Modal } = await import("bootstrap");
  bsModal = new Modal(modalEl.value);
  modalEl.value.addEventListener("hidden.bs.modal", () => {
    if (props.modelValue) emit("update:modelValue", false);
  });
  if (props.modelValue) bsModal.show();
});

onBeforeUnmount(() => {
  bsModal?.hide();
});

watch(
  () => props.modelValue,
  (open) => {
    if (!bsModal) return;
    open ? bsModal.show() : bsModal.hide();
  },
);
</script>

<style scoped>
.cart-error__icon {
  color: var(--bs-warning);
  font-size: 1.6rem;
  line-height: 1;
}

.last-mb-0:last-child {
  margin-bottom: 0 !important;
}
</style>
